import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { favoritesApi } from '@/modules/profile/api/favorites.api'
import { getRecommendedProducts } from '@/modules/catalog/public'
import { useAuthSession } from '@/modules/auth/public'
import type { Product } from '@/types/catalog'

const LOCAL_STORAGE_KEY = 'labactive_favorites'

function loadLocalFavorites(): Product[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveLocalFavorites(items: Product[]) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items))
  } catch {
    // ignore
  }
}

export const useFavoritesStore = defineStore('favorites', () => {
  // Состояние
  const favorites = ref<Product[]>([])
  const loading = ref(false)
  const showAuthPrompt = ref(false)

  // Публичный контракт auth-сессии, а не внутренний store
  const authSession = useAuthSession()

  // Вычисляемые свойства
  const favoritesCount = computed(() => favorites.value.length)

  const isFavorite = (productId: string | number) => {
    if (!Array.isArray(favorites.value)) {
      return false
    }
    return favorites.value.some((product) => String(product.id) === String(productId))
  }

  const isAuthenticated = computed(() => authSession.isAuthenticated.value)

  // Методы
  const loadFavorites = async () => {
    loading.value = true
    try {
      const result = await favoritesApi.getFavorites()
      // Убеждаемся, что результат является массивом
      favorites.value = Array.isArray(result) ? result : []
    } catch (error) {
      console.error('Ошибка загрузки избранного:', error)
      favorites.value = []
    } finally {
      loading.value = false
    }
  }

  const addToFavorites = async (product: Product) => {
    try {
      const success = await favoritesApi.addToFavorites(product)
      if (success) {
        await loadFavorites() // Перезагружаем список
      }
      return success
    } catch (error) {
      console.error('Ошибка добавления в избранное:', error)
      return false
    }
  }

  const mergeGuestFavorites = async () => {
    const guestFavorites = loadLocalFavorites()

    if (guestFavorites.length === 0) {
      await loadFavorites()
      return
    }

    for (const product of guestFavorites) {
      try {
        await favoritesApi.addToFavorites(product)
      } catch (error) {
        console.error('Ошибка переноса гостевого избранного:', error)
      }
    }

    saveLocalFavorites([])
    await loadFavorites()
  }

  const removeFromFavorites = async (productId: string) => {
    try {
      const success = await favoritesApi.removeFromFavorites(productId)
      if (success) {
        await loadFavorites() // Перезагружаем список
      }
      return success
    } catch (error) {
      console.error('Ошибка удаления из избранного:', error)
      return false
    }
  }

  const toggleFavorite = async (product: Product) => {
    // Для незалогиненных - работаем с localStorage
    if (!authSession.isAuthenticated.value) {
      const current = loadLocalFavorites()
      const exists = current.some((p) => String(p.id) === String(product.id))

      if (exists) {
        const filtered = current.filter((p) => String(p.id) !== String(product.id))
        saveLocalFavorites(filtered)
        favorites.value = filtered
        return { requiresAuth: false, isFavorite: false, removed: true }
      } else {
        const added = [...current, product]
        saveLocalFavorites(added)
        favorites.value = added
        return { requiresAuth: false, isFavorite: true, added: true }
      }
    }

    // Для залогиненных - API
    try {
      const result = await favoritesApi.toggleFavorite(product)
      await loadFavorites() // Перезагружаем список
      return { requiresAuth: false, isFavorite: result.isFavorite }
    } catch (error) {
      console.error('Ошибка переключения избранного:', error)
      return { requiresAuth: false, isFavorite: false }
    }
  }

  const addToFavoritesWithAuth = async (product: Product) => {
    // Проверяем авторизацию
    if (!authSession.isAuthenticated.value) {
      showAuthPrompt.value = true
      return { requiresAuth: true, success: false }
    }

    try {
      const success = await addToFavorites(product)
      return { success, requiresAuth: false }
    } catch (error) {
      console.error('Ошибка добавления в избранное:', error)
      return { success: false, requiresAuth: false }
    }
  }

  const closeAuthPrompt = () => {
    showAuthPrompt.value = false
  }

  const clearFavorites = async () => {
    // Для незалогиненных - очищаем localStorage
    if (!authSession.isAuthenticated.value) {
      saveLocalFavorites([])
      favorites.value = []
      return true
    }

    try {
      const success = await favoritesApi.clearFavorites()
      if (success) {
        favorites.value = []
      }
      return success
    } catch (error) {
      console.error('Ошибка очистки избранного:', error)
      return false
    }
  }

  const clearAll = async () => {
    return clearFavorites()
  }

  const fetchRecommended = async (): Promise<Product[]> => {
    try {
      // catalog/public уже отдаёт нормализованный UI Product (Money/stock/категория
      // смаппены внутри catalog/api) - favorites только исключает уже избранные товары.
      const transformed = await getRecommendedProducts(4)
      const favoriteIds = new Set(favorites.value.map((p) => String(p.id)))
      return transformed.filter((p) => !favoriteIds.has(String(p.id)))
    } catch {
      return []
    }
  }

  const getFavoritesCount = async () => {
    try {
      return await favoritesApi.getFavoritesCount()
    } catch (error) {
      console.error('Ошибка получения количества избранного:', error)
      return 0
    }
  }

  // Инициализация при создании store
  const initializeStore = () => {
    // Убеждаемся, что favorites всегда является массивом
    if (!Array.isArray(favorites.value)) {
      favorites.value = []
    }

    if (authSession.isAuthenticated.value) {
      loadFavorites()
    } else {
      // Загружаем из localStorage для незалогиненных
      favorites.value = loadLocalFavorites()
    }
  }

  // Вызываем инициализацию
  initializeStore()

  return {
    // Состояние
    favorites,
    loading,
    showAuthPrompt,

    // Вычисляемые свойства
    favoritesCount,
    isFavorite,
    isAuthenticated,

    // Методы
    loadFavorites,
    mergeGuestFavorites,
    addToFavorites,
    addToFavoritesWithAuth,
    removeFromFavorites,
    toggleFavorite,
    clearFavorites,
    clearAll,
    fetchRecommended,
    getFavoritesCount,
    closeAuthPrompt,
    initializeStore,
  }
})
